import { useContext, useMemo } from 'react';
import { AppStateContext, Translate, formatMoney } from '@water-web/view';
import { useTranslation } from 'react-i18next';

import { Button, Text } from '@app/components/atoms';
import { CartModeItemProps } from '@app/contexts/CartController/types';
import { useCheckoutFlow } from '@app/contexts/CheckoutFlow';
import { toaster } from '@app/components/molecules/Toaster';
import { isNullish } from '@app/utils/null-checks';

const checkIsValidAmount = (amount: number | undefined, minAmount: number | undefined): boolean => {
  if (isNullish(amount) || isNullish(minAmount)) {
    return false;
  }
  return amount >= minAmount;
};

const getAmount = (minAmount: number | undefined, currency: string | undefined): string => {
  if (isNullish(minAmount) || isNullish(currency)) {
    return '';
  }
  return formatMoney(minAmount, currency);
};

export const GiftCardControls = (props: CartModeItemProps) => {
  const appState = useContext(AppStateContext);
  const checkoutFlow = useCheckoutFlow();
  const { t } = useTranslation();

  const reservation = props.cart?.getActiveReservation();
  const giftCardAmount = reservation?.getGiftCardSaleAmount();
  const minGiftCardAmount = reservation?.getMinGiftCardAmount();

  const isValidAmount = checkIsValidAmount(giftCardAmount, minGiftCardAmount);

  const onButtonClick = useMemo(() => {
    if (!isValidAmount) {
      return () => {
        const currency = reservation?.getShop()?.getCurrency();
        toaster.error(
          t('pages.giftCard.minAmountError', {
            amount: getAmount(minGiftCardAmount, currency),
          }),
        );
      };
    }

    if (props.expanded) {
      return checkoutFlow.next;
    }

    return () => {
      props.setCartState('expanded');
    };
  }, [isValidAmount, props, reservation, t, minGiftCardAmount, checkoutFlow.next]);

  return (
    <Button
      height="48px"
      width="100%"
      onClick={onButtonClick}
      disabled={!appState.cart.interactive || !giftCardAmount}
      $testId="btn:submit"
    >
      <Text.P2Bold>
        <Translate i18nKey="pages.giftCard.continue" />
      </Text.P2Bold>
    </Button>
  );
};
